import { lazy, Suspense, useState, useMemo, useCallback, useRef } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import { useIsHomeDisplayReady } from "@/modules/homeDisplay/hooks";
import type { RedirectConfigItem } from "@/modules/homeDisplay/types";
import { getCountryRedirectInfoWithCache } from "@/utils/countryRedirectInfoCache";
import { Message } from "@/components/Base";
import {
  hasCountryIdStored,
  getCountryIdFromStorage,
  setCountryIdToStorage,
  setRedirectInfoCookie,
} from "@/utils/countryCookie";
import utils from "@/utils/utils";

/** 是否有国家选择记录（cookie 或 localStorage，有则不再弹窗） */
export const hasCountryCookie = (): boolean => hasCountryIdStored();

const CountryListDialog = lazy(() =>
  import("../../CountryListDialog/CountryListDialog").then(m => ({
    default: m.default,
  }))
);

export const useCountryListDialog = () => {
  const isConfigReady = useIsHomeDisplayReady();
  const redirectConfigs = useHomeDisplayStore(
    state => state.data?.redirectConfigs ?? []
  );
  const hasList = useMemo(
    () => Array.isArray(redirectConfigs) && redirectConfigs.length > 0,
    [redirectConfigs]
  );
  const dismissedThisSessionRef = useRef(false);

  const CountryListDialogContent: React.FC<{
    onComplete: () => void;
    showCloseButton?: boolean;
  }> = ({ onComplete, showCloseButton: showClose = false }) => {
    const [visible, setVisible] = useState(true);
    const handleClose = useCallback(() => {
      dismissedThisSessionRef.current = true;
      setVisible(false);
      onComplete();
    }, [onComplete]);

    const handleItemClick = useCallback(
      async (item: RedirectConfigItem) => {
        const countryCode = item?.countryRedirectId;
        if (!countryCode) {
          handleClose();
          return;
        }
        const currentCountryId =
          (utils.getObject("countryId") as string) ||
          getCountryIdFromStorage() ||
          "";
        const isCurrentCountry =
          currentCountryId.trim().toUpperCase() ===
          String(countryCode).trim().toUpperCase();
        // 选中的就是当前国家：只把 selectedCountryId 存 localStorage，不写 cookie，不跳转
        if (isCurrentCountry) {
          setCountryIdToStorage(currentCountryId);
          utils.setObject("countryId", currentCountryId);
          handleClose();
          return;
        }
        try {
          const info = await getCountryRedirectInfoWithCache(
            countryCode,
            window.location.search
          );
          if (!info?.redirectUrl) {
            handleClose();
            return;
          }
          // 跳转 URL 添加 _setCookie，由目标页设置 cookie
          const separator = info.redirectUrl.includes("?") ? "&" : "?";
          let targetUrl = `${info.redirectUrl}${separator}_setCookie=${encodeURIComponent(info.cookieValue)}`;
          // 若有邀请码，带上 p=xxx
          const inviteCode = utils.getObject("invitedCode") as string;
          if (inviteCode && String(inviteCode).trim()) {
            targetUrl += `&p=${encodeURIComponent(String(inviteCode).trim())}`;
          }
          const fromDomain = window.location.host;
          const fromCountry =
            (utils.getObject("countryId") as string) ||
            getCountryIdFromStorage() ||
            "";
          setRedirectInfoCookie(fromDomain, fromCountry);
          targetUrl += `&from_domain=${encodeURIComponent(fromDomain)}&from_country=${encodeURIComponent(fromCountry)}`;
          window.location.href = targetUrl;
        } catch (e: any) {
          Message.failed(e?.message || "failed");
          handleClose();
        }
      },
      [handleClose]
    );

    return (
      <Suspense fallback={null}>
        <CountryListDialog
          visible={visible}
          onClose={handleClose}
          onItemClick={handleItemClick}
          showCloseButton={showClose}
        />
      </Suspense>
    );
  };

  useQueuedPopup({
    popupId: POPUP_CONFIGS.COUNTRY_LIST.id,
    priority: POPUP_CONFIGS.COUNTRY_LIST.priority,
    shouldShow: context => {
      const baseAuto =
        isConfigReady && hasList && !dismissedThisSessionRef.current;
      if (context?.isManualTrigger) {
        return isConfigReady && hasList;
      }
      return baseAuto && !hasCountryCookie();
    },
    renderContent: (onComplete, context) => (
      <CountryListDialogContent
        onComplete={onComplete}
        showCloseButton={!!context?.isManualTrigger}
      />
    ),
    showOnce: false,
    allowManualTrigger: true, // 允许左下角浮动图标等通过 #country-list-dialog 手动打开
    dependencies: [isConfigReady, hasList],
    timingType: "ENTER_HOME_PAGE",
  });
};
